import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API_URL, formatApiError } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { ArrowsClockwise, CreditCard, Eye } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { DataToolbar } from '../components/DataToolbar';
import { DataTable } from '../components/DataTable';
import { DataPagination } from '../components/DataPagination';

const fmtFee = (v) => {
  const n = Number(v || 0);
  if (!isFinite(n)) return '-';
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtDate = (s) => {
  if (!s) return '-';
  const d = new Date(String(s).replace(' ', 'T'));
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
};

function DetailRow({ label, value, mono = false }) {
  return (
    <div className="flex flex-col gap-1 py-2 border-b border-slate-100 last:border-b-0">
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
        {label}
      </span>
      <span className="text-sm text-[#1C1917]" style={{ fontFamily: mono ? 'JetBrains Mono, monospace' : 'Plus Jakarta Sans, sans-serif' }}>
        {value || '-'}
      </span>
    </div>
  );
}

export default function CardRenewalsPage() {
  const { isSuperAdmin } = useAuth();
  const { entities } = useTenant();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [phoneNameMap, setPhoneNameMap] = useState({});

  // Build a phone(last-9-digits) → display-name map from cashiers + users
  const fetchPhoneNameMap = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const tailOf = (mob) => {
      const d = String(mob || '').replace(/\D/g, '');
      return d.length >= 9 ? d.slice(-9) : d;
    };
    const map = {};
    try {
      const [cRes, uRes] = await Promise.all([
        axios.get(`${API_URL}/api/cashiers`, { headers, withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/users`,    { headers, withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      (Array.isArray(cRes.data) ? cRes.data : []).forEach(c => {
        const tail = tailOf(c.user_mobile);
        if (tail) map[tail] = c.user_fullname || c.user_name || tail;
      });
      (Array.isArray(uRes.data) ? uRes.data : []).forEach(u => {
        const tail = tailOf(u.mobile);
        if (tail && !map[tail]) map[tail] = u.fullname || u.username || tail;
      });
    } catch (e) { /* non-blocking */ }
    setPhoneNameMap(map);
  }, []);

  const nameForPhone = useCallback((mob) => {
    const d = String(mob || '').replace(/\D/g, '');
    if (!d) return '-';
    const tail = d.length >= 9 ? d.slice(-9) : d;
    return phoneNameMap[tail] || mob;
  }, [phoneNameMap]);

  const fetchRenewals = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('page_size', String(pageSize));
      if (isSuperAdmin && entityFilter && entityFilter !== 'all') {
        params.set('entity_id', entityFilter);
      }
      const { data } = await axios.get(`${API_URL}/api/card-renewals?${params.toString()}`, {
        withCredentials: true,
        headers,
      });
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setTotal(Number(data?.total || 0));
      setTotalPages(Number(data?.total_pages || 1));
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load card renewals'));
      setRows([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, entityFilter, isSuperAdmin]);

  useEffect(() => { fetchRenewals(); }, [fetchRenewals]);
  useEffect(() => { fetchPhoneNameMap(); }, [fetchPhoneNameMap]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.sub_name, r.old_card, r.new_card, r.related_agent, r.maker_id, r.entity_name, r.campus_name]
        .some((v) => String(v || '').toLowerCase().includes(q))
    );
  }, [rows, search]);

  const columns = useMemo(() => [
    { key: 'sub_name',     label: 'Subscriber', width: 'minmax(200px,2fr)',   tone: 'strong', render: (r) => r.sub_name || `#${r.sub_id}` },
    { key: 'old_card',     label: 'Old Card',   width: 'minmax(140px,1.2fr)', mono: true,    tone: 'muted' },
    { key: 'new_card',     label: 'New Card',   width: 'minmax(140px,1.2fr)', mono: true,    tone: 'strong' },
    { key: 'plan',         label: 'Plan',       width: 'minmax(100px,0.9fr)' },
    { key: 'fee',          label: 'Fee',        width: 'minmax(110px,1fr)',   mono: true, align: 'right', tone: 'strong', render: (r) => fmtFee(r.fee) },
    { key: 'date_changed', label: 'Date',       width: 'minmax(170px,1.3fr)', mono: true,    tone: 'muted', render: (r) => fmtDate(r.date_changed) },
  ], []);

  return (
    <div className="space-y-3 pb-6" data-testid="card-renewals-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-[#1C1917] tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          Card Renewals
        </h1>
        <Button
          variant="outline"
          onClick={() => { setPage(1); fetchRenewals(); }}
          data-testid="refresh-renewals"
          className="border-[#EDE6D8]"
        >
          <ArrowsClockwise size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Toolbar */}
      <DataToolbar
        value={search}
        onChange={setSearch}
        placeholder="Search name, card, agent…"
        testId="renewals-toolbar"
        onClearAll={() => { setSearch(''); setEntityFilter('all'); }}
        filters={isSuperAdmin ? [{
          key: 'entity',
          label: 'Entity',
          value: entityFilter,
          onChange: (v) => { setEntityFilter(v); setPage(1); },
          options: [{ value: 'all', label: 'All entities' }, ...entities.map((e) => ({ value: e.id, label: e.entity_name }))],
        }] : []}
      />

      <DataTable
        columns={columns}
        rows={filteredRows}
        loading={loading}
        rowKey={(r) => `${r.sub_id}-${r.date_changed || ''}-${r.new_card || ''}`}
        onRowClick={(r) => setSelected(r)}
        emptyIcon={<CreditCard size={20} className="text-slate-500" />}
        emptyTitle="No card renewals found"
        emptyText={search ? 'Try a different search term.' : 'Renewals will appear here once cards are reissued.'}
        testIdPrefix="renewals"
        actionRender={(r) => (
          <span className="flex items-center justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); setSelected(r); }}
              className="text-[#0A2540] hover:text-[#0A2540] hover:bg-[#0A2540]/10 group-hover:text-[#1C1917] group-hover:bg-[#EDE6D8] h-8 w-8 p-0"
              data-testid={`view-renewal-${r.sub_id || ''}-${r.new_card || ''}`}
              aria-label="View renewal"
            >
              <Eye size={14} />
            </Button>
          </span>
        )}
      />

      <DataPagination
        page={page}
        pageSize={pageSize}
        total={total}
        totalPages={totalPages}
        onPageChange={setPage}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
        testId="renewals-pagination"
      />

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <DialogContent className="max-w-lg" data-testid="renewal-detail-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Renewal Detail</DialogTitle>
            <DialogDescription className="sr-only">Full details of the selected card renewal</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-1">
              <DetailRow label="Subscriber" value={selected.sub_name || `#${selected.sub_id}`} />
              <div className="grid grid-cols-2 gap-4">
                <DetailRow label="Old Card" value={selected.old_card} mono />
                <DetailRow label="New Card" value={selected.new_card} mono />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <DetailRow label="Plan" value={selected.plan} />
                <DetailRow label="Fee" value={fmtFee(selected.fee)} mono />
              </div>
              <DetailRow label="Date Changed" value={fmtDate(selected.date_changed)} mono />
              <div className="grid grid-cols-2 gap-4">
                {isSuperAdmin ? (
                  <DetailRow label="Entity" value={selected.entity_name} />
                ) : null}
                <DetailRow label="Campus" value={selected.campus_name} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <DetailRow label="Related Agent" value={nameForPhone(selected.related_agent)} />
                <DetailRow label="Maker" value={nameForPhone(selected.maker_id)} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
